import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const WebSocketConfigSection = () => {
  const [wsConfig, setWsConfig] = useState({
    enabled: true,
    autoReconnect: true,
    reconnectInterval: 5000,
    maxReconnectAttempts: 10,
    heartbeatInterval: 30000,
    compressionEnabled: true
  });

  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastConnected, setLastConnected] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [latency, setLatency] = useState(null);

  const intervalOptions = [
    { value: 1000, label: '1 second' },
    { value: 3000, label: '3 seconds' },
    { value: 5000, label: '5 seconds' },
    { value: 10000, label: '10 seconds' },
    { value: 30000, label: '30 seconds' }
  ];

  const heartbeatOptions = [
    { value: 15000, label: '15 seconds' },
    { value: 30000, label: '30 seconds' },
    { value: 60000, label: '1 minute' },
    { value: 120000, label: '2 minutes' }
  ];

  useEffect(() => {
    // Simulate WebSocket connection status
    const statusInterval = setInterval(() => {
      const statuses = ['connected', 'connecting', 'disconnected', 'error'];
      const weights = [0.7, 0.1, 0.15, 0.05]; // Probability weights
      
      let random = Math.random();
      let selectedStatus = statuses?.[0];
      
      for (let i = 0; i < statuses?.length; i++) {
        if (random < weights?.[i]) {
          selectedStatus = statuses?.[i];
          break;
        }
        random -= weights?.[i];
      }
      
      setConnectionStatus(selectedStatus);
      
      if (selectedStatus === 'connected') {
        setLastConnected(new Date());
        setLatency(Math.floor(Math.random() * 100) + 20); // 20-120ms
        setReconnectAttempts(0);
      } else if (selectedStatus === 'connecting') {
        setReconnectAttempts(prev => prev + 1);
      }
    }, 8000);

    return () => clearInterval(statusInterval);
  }, []);

  const handleConfigChange = (field, value) => {
    setWsConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConnect = () => {
    setConnectionStatus('connecting');
    setReconnectAttempts(0);
    
    // Simulate connection attempt
    setTimeout(() => {
      setConnectionStatus('connected');
      setLastConnected(new Date());
      setLatency(Math.floor(Math.random() * 50) + 25);
    }, 2000);
  };

  const handleDisconnect = () => {
    setConnectionStatus('disconnected');
    setLatency(null);
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'text-success';
      case 'connecting':
        return 'text-warning';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Wifi';
      case 'connecting':
        return 'Loader';
      case 'error':
        return 'WifiOff';
      default:
        return 'WifiOff';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Connection Error';
      default:
        return 'Disconnected';
    }
  };

  const formatTimestamp = (date) => {
    if (!date) return 'Never';
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 security-shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Radio" size={20} className="text-primary" />
            <span>WebSocket Configuration</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time communication settings for live updates and notifications
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {connectionStatus === 'connected' ? (
            <Button
              variant="outline"
              size="sm"
              iconName="WifiOff"
              onClick={handleDisconnect}
              className="text-error hover:text-error"
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              iconName="Wifi"
              onClick={handleConnect}
              loading={connectionStatus === 'connecting'}
            >
              Connect
            </Button>
          )}
        </div>
      </div>
      {/* Connection Status */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Icon 
              name={getStatusIcon()} 
              size={20} 
              className={`${getStatusColor()} ${connectionStatus === 'connecting' ? 'animate-spin' : ''} ${connectionStatus === 'connected' ? 'security-pulse' : ''}`}
            />
            <div>
              <h4 className="text-sm font-medium text-foreground">Connection Status</h4>
              <p className={`text-sm ${getStatusColor()}`}>{getStatusText()}</p>
            </div>
          </div>
          
          {latency && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Latency</p>
              <p className="text-sm font-medium text-success">{latency}ms</p>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div>
            <p className="text-muted-foreground">Last Connected</p>
            <p className="text-foreground font-medium">{formatTimestamp(lastConnected)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Reconnect Attempts</p>
            <p className="text-foreground font-medium">{reconnectAttempts}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Configuration</p>
            <p className="text-foreground font-medium">{wsConfig?.enabled ? 'Enabled' : 'Disabled'}</p>
          </div>
        </div>
      </div>
      {/* WebSocket Settings */}
      <div className="space-y-6">
        <div>
          <h4 className="text-md font-medium text-foreground mb-4">Connection Settings</h4>
          <div className="space-y-4">
            <Checkbox
              label="Enable WebSocket connection"
              description="Allow real-time communication for live updates"
              checked={wsConfig?.enabled}
              onChange={(e) => handleConfigChange('enabled', e?.target?.checked)}
            />
            
            <Checkbox
              label="Auto-reconnect"
              description="Automatically reconnect when connection is lost"
              checked={wsConfig?.autoReconnect}
              onChange={(e) => handleConfigChange('autoReconnect', e?.target?.checked)}
            />
            
            <Checkbox
              label="Enable compression"
              description="Compress WebSocket messages to reduce bandwidth"
              checked={wsConfig?.compressionEnabled}
              onChange={(e) => handleConfigChange('compressionEnabled', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Advanced Settings */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Settings" size={16} className="text-accent" />
            <span>Advanced Settings</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Reconnect Interval"
              description="Time between reconnection attempts"
              options={intervalOptions}
              value={wsConfig?.reconnectInterval}
              onChange={(value) => handleConfigChange('reconnectInterval', parseInt(value))}
            />
            
            <Input
              label="Max Reconnect Attempts"
              type="number"
              min="1"
              max="50"
              value={wsConfig?.maxReconnectAttempts}
              onChange={(e) => handleConfigChange('maxReconnectAttempts', parseInt(e?.target?.value))}
              description="Maximum reconnection attempts before giving up"
            />
            
            <Select
              label="Heartbeat Interval"
              description="Keep-alive ping frequency"
              options={heartbeatOptions}
              value={wsConfig?.heartbeatInterval}
              onChange={(value) => handleConfigChange('heartbeatInterval', parseInt(value))}
              className="md:col-span-2"
            />
          </div>
        </div>

        {/* Real-time Features */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Zap" size={16} className="text-warning" />
            <span>Real-time Features</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Bell" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Live Notifications</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Security alerts</li>
                <li>• System status changes</li>
                <li>• User activity updates</li>
                <li>• Device status changes</li>
              </ul>
            </div>
            
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Activity" size={16} className="text-accent" />
                <span className="text-sm font-medium text-foreground">Live Data</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Lock status updates</li>
                <li>• Access attempt logs</li>
                <li>• Analytics data</li>
                <li>• System metrics</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Connection Quality */}
        <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
          <h5 className="text-sm font-medium text-accent mb-3 flex items-center space-x-2">
            <Icon name="Signal" size={16} />
            <span>Connection Quality</span>
          </h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-muted-foreground">Status</p>
              <p className={`font-medium ${getStatusColor()}`}>{getStatusText()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Latency</p>
              <p className="font-medium text-foreground">{latency ? `${latency}ms` : 'N/A'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Compression</p>
              <p className="font-medium text-foreground">{wsConfig?.compressionEnabled ? 'Enabled' : 'Disabled'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Auto-reconnect</p>
              <p className="font-medium text-foreground">{wsConfig?.autoReconnect ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebSocketConfigSection;